import type { AdminCapabilities, AdminRole } from '@/lib/adminApi';

/**
 * Rank of each admin role, least to most privileged — the SPA's mirror of
 * apps/api-py/app/models/admin_role.py:admin_role_at_least (ClickUp
 * 869f0ev24). Kept as a small, independent copy rather than importing
 * anything cross-language: the SOURCE of truth for what a role may do is
 * always the API's own role check on each route (this app hides a button a
 * role cannot use; it does not grant it), so a drift here only ever hides a
 * button that would otherwise 403 — never the reverse.
 */
const RANK: Record<AdminRole, number> = {
  // `viewer` is the LOWEST rank: it reads every screen and acts on nothing
  // (ClickUp 869f0ev3c). Same order as admin_role.py's ADMIN_ROLES.
  viewer: 0,
  support: 1,
  finance: 2,
  superadmin: 3,
};

export function roleAtLeast(role: AdminRole | undefined, minimum: AdminRole): boolean {
  if (!role) return false;
  return RANK[role] >= RANK[minimum];
}

/**
 * May this console account move credits? The SPA's mirror of
 * apps/api-py/app/api/admin_credits.py:CREDIT_ROLES (support, finance,
 * superadmin — no cap at launch, proposal §7 decision 5); a `viewer` reads
 * every screen and acts on nothing, and the API refuses its writes in one
 * place anyway (app/http/deps.py:_refuse_viewer_write).
 *
 * It lives here, next to `roleAtLeast`, because TWO screens ask it now — the
 * grant form on /organizations/:orgId/credits and the link to that form on a
 * user's card — and a second inline `role === 'support' || …` list is exactly
 * how one of them ends up offering a read-only account a control that 403s.
 */
export function canGrantCredits(role: AdminRole | undefined): boolean {
  return role === 'support' || role === 'finance' || role === 'superadmin';
}

/**
 * May this console account change a person's login email? Every write role —
 * the SPA's mirror of apps/api-py/app/models/admin_role.py:
 * ACCOUNT_EMAIL_CHANGE_ROLES (ClickUp 869f4a4a0): the same tier as the
 * password reset it is equivalent to in power.
 */
export function canChangeEmail(role: AdminRole | undefined): boolean {
  return roleAtLeast(role, 'support');
}

/**
 * May this console account merge one account into another, and may it delete
 * one? `support` and `superadmin` — the SPA's mirror of
 * apps/api-py/app/models/admin_role.py:ACCOUNT_TRANSFER_ROLES and
 * ACCOUNT_DELETION_ROLES.
 *
 * THE OWNER WIDENED BOTH ON 21.09.2026 (ClickUp 869f4a4a0), reversing the
 * "superadmin only — a transfer ends a login, the same threshold as deletion"
 * line that stood here since 19.09. The API is the source of truth and refuses
 * with 403 regardless; these two only decide what is rendered.
 *
 * NOT `roleAtLeast(role, 'support')`: that also admits `finance`, which the
 * decision does not grant. The grant is a SET, and it is spelled as one on
 * both sides.
 */
const TRANSFER_AND_DELETE_ROLES: readonly AdminRole[] = ['support', 'superadmin'];

export function canTransferAccount(role: AdminRole | undefined): boolean {
  return !!role && TRANSFER_AND_DELETE_ROLES.includes(role);
}

/** The deletion door — same line, same decision, its own name because the two
 * are two decisions that agree today, not one read twice. */
export function canDeleteAccount(role: AdminRole | undefined): boolean {
  return !!role && TRANSFER_AND_DELETE_ROLES.includes(role);
}

/**
 * THE OWNER'S LINE (19.09.2026, ClickUp 869f4c8v4): support gets exactly
 * what the support team asked for and nothing more; a user's CHATS, a user's
 * content ITSELF (the file, not the row) and analytics about users are the
 * superadmin's alone. The three helpers below mirror
 * apps/api-py/app/models/admin_role.py:ADMIN_ACTION_MIN_ROLE — the API
 * refuses with 403 regardless; these only decide what to render.
 */

/** `admin.user_chats.read` — the Chats tab on a user's card. */
export function canReadUserChats(role: AdminRole | undefined): boolean {
  return roleAtLeast(role, 'superadmin');
}

/** `admin.user_media.open` — thumbnails, the full-size preview and chat
 * attachments (everything that goes through POST /api/admin/v2/media/sign).
 * Every role keeps the content ROWS. */
export function canOpenUserMedia(role: AdminRole | undefined): boolean {
  return roleAtLeast(role, 'superadmin');
}

/** `admin.user_reports.read` — the Reports page (aggregates about users). */
export function canReadUserReports(role: AdminRole | undefined): boolean {
  return roleAtLeast(role, 'superadmin');
}

/** `admin.spend_reports.read` — the Spend page (what generations cost us; users
 * as aggregates only). Every console role from `viewer` up. */
export function canReadSpend(role: AdminRole | undefined): boolean {
  return roleAtLeast(role, 'viewer');
}

/**
 * Capability names the API adds to GET /api/admin-auth/me for the usage
 * reports and the user-workflows viewer. ONE place for the strings: if the
 * backend spells either differently, only these two constants change.
 */
export const CAPABILITY_USAGE_REPORTS = 'usage_reports_read' as const;
export const CAPABILITY_WORKFLOWS = 'workflows_read' as const;

type CapabilityBag = Partial<AdminCapabilities> | undefined;

function hasCapability(capabilities: CapabilityBag, name: keyof AdminCapabilities): boolean {
  return capabilities?.[name] === true;
}

/** The four spend reports (LLM models / LLM by user / Top spenders /
 * Generations): the API's capability when it sends one, else superadmin. */
export function canReadUsageReports(role: AdminRole | undefined, capabilities?: CapabilityBag): boolean {
  return hasCapability(capabilities, CAPABILITY_USAGE_REPORTS) || roleAtLeast(role, 'superadmin');
}

/** The "User workflows" section: another person's workflow, runs, outputs and
 * chats, read-only. Same decision as the reports: capability, else superadmin. */
export function canReadUserWorkflows(role: AdminRole | undefined, capabilities?: CapabilityBag): boolean {
  return hasCapability(capabilities, CAPABILITY_WORKFLOWS) || roleAtLeast(role, 'superadmin');
}
